import { Component, ElementRef, ViewChild, HostListener, input, model, AfterContentInit, signal, forwardRef, contentChildren, output, computed, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Rarity, MinimapIconShape, Color, MinimapIconSize, GearSizeType } from "../../models/enums";
import { BaseTypeTier } from "../../models/enums";
import { MinimapIconComponent } from "../icons/minimap-icon/minimap-icon.component";
import { GearTierIconComponent } from "../icons/gear-tier-icon/gear-tier-icon.component";
import { ValueComponent } from "../value/value.component";
import { ItemDataService } from "../../services/item-data.service";
import { BaseComponent } from "../base.component";
import { EconomyService } from "../../services/economy.service";
import { PricedItem } from "../../models/economy-models";

export type DisplayMode = 'textOnly' | 'iconOnly' | 'prefixAndText' | 'textAndSuffix';

// Single option component with all possible input parameters
@Component({
  standalone: true,
  selector: 'app-option',
  template: '<ng-content></ng-content>',
})
export class SelectOptionComponent {
  value = input.required<any>();
  text = input.required<string>();
  dropdownText = input<string>();

  // Optional content inputs
  imgUrl = input<string>('');
  imgSet = input<string[] | undefined>(undefined);
  alt = input<string>('');
  width = input<string>('16px');
  height = input<string>('16px');

  // Component type inputs
  rarity = input<Rarity | undefined>(undefined);
  gearTier = input<BaseTypeTier | undefined>(undefined);
  modName = input<string>('');
  minimapIcon = input<MinimapIconShape | undefined>(undefined);

  // Styling
  color = input<string>('');

  // Comparator inputs for displaying ≥ and ≤ signs
  gte = input<boolean>(false);
  lte = input<boolean>(false);
  eq = input<boolean>(false);

  // Check if this option has any custom content
  get hasCustomContent(): boolean {
    return !!this.imgUrl() || !!this.rarity() || !!this.gearTier() || !!this.minimapIcon();
  }
}

@Component({
  standalone: true,
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss',
  imports: [FormsModule, MinimapIconComponent, GearTierIconComponent, ValueComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true
    }
  ]
})
export class SelectComponent extends BaseComponent implements AfterContentInit, ControlValueAccessor, OnInit {
  disabled = input<boolean>(false);
  searchable = input<boolean>(false);
  selectedValue = model<any>(undefined);
  displayMode = input<DisplayMode | undefined>(undefined);
  resolvedDisplayMode = computed(() =>
    this.displayMode() ?? (this.settings.hideIconsInDropdowns() ? 'textOnly' : 'prefixAndText')
  );
  fullWidth = input<boolean>(false);
  alignLeft = input<boolean>(false);
  hideHoverInfo = input<boolean>(false);

  selectedValueChange = output<any>();

  optionsContentChildren = contentChildren(SelectOptionComponent);

  @ViewChild('searchInput') searchInput!: ElementRef;
  @ViewChild('optionsContainer') optionsContainer!: ElementRef;

  open = signal(false);
  searchText = signal('');
  highlightedIndex = signal<number>(-1);

  // Hover-related properties
  extraInfo?: string[] = undefined;
  value?: PricedItem = undefined;

  private onChange = (value: any) => {};
  private onTouched = () => {};

  filteredOptions = computed(() => {
    const options = this.optionsContentChildren();
    const searchTextValue = this.searchText();
    if (!searchTextValue || !this.searchable()) return options;

    return options.filter(option => {
      const searchLower = searchTextValue.toLowerCase();
      if (option.text().toLowerCase().includes(searchLower)) return true;
      const name = option.modName();
      return !!name && name.toLowerCase().includes(searchLower);
    });
  });

  selectedOption = computed(() => {
    return this.optionsContentChildren().find(opt => opt.value() === this.selectedValue());
  });

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    if (this.open() && !this.elementRef.nativeElement.contains(event.target)) {
      this.open.set(false);
      this.highlightedIndex.set(-1);
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (this.disabled()) return;

    const isOpen = this.open();
    const options = this.filteredOptions();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          this.toggleDropdown();
          this.highlightedIndex.set(0);
        } else {
          this.highlightedIndex.update(current => {
            if (options.length === 0) return -1;
            const newIndex = current < options.length - 1 ? current + 1 : current;
            // Immediately select the option without closing dropdown
            if (newIndex >= 0 && newIndex < options.length) {
              this.selectOption(options[newIndex], true);
            }
            return newIndex;
          });
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (!isOpen) {
          this.toggleDropdown();
          this.highlightedIndex.set(options.length - 1);
        } else {
          this.highlightedIndex.update(current => {
            if (options.length === 0) return -1;
            const newIndex = current > 0 ? current - 1 : current;
            // Immediately select the option without closing dropdown
            if (newIndex >= 0 && newIndex < options.length) {
              this.selectOption(options[newIndex], true);
            }
            return newIndex;
          });
        }
        break;

      case 'Enter':
        event.preventDefault();
        if (isOpen && this.highlightedIndex() >= 0 && this.highlightedIndex() < options.length) {
          this.selectOption(options[this.highlightedIndex()]);
        } else if (!isOpen) {
          this.toggleDropdown();
        }
        break;

      case 'Escape':
        event.preventDefault();
        if (isOpen) {
          this.open.set(false);
          this.highlightedIndex.set(-1);
        }
        break;

      case 'Tab':
        if (isOpen) {
          this.open.set(false);
          this.highlightedIndex.set(-1);
        }
        break;
    }
  }

  constructor(private elementRef: ElementRef, private itemDataService: ItemDataService, private economyService: EconomyService) {
    super();
  }

  ngOnInit() {
    // No initialization needed
  }

  ngAfterContentInit() {
    // No text extraction needed - everything is explicit now
  }

  getMinimapIconOptions(shape: MinimapIconShape) {
    return {
      highlight: true,
      highlightColor: Color.White,
      minimapIconShape: shape,
      minimapIconSize: MinimapIconSize.Medium
    };
  }

  hovered(optionText: string) {
    if (this.hideHoverInfo()) {
      return;
    }

    const currencyData = this.itemDataService.getCurrency(optionText);
    this.extraInfo = currencyData?.effects;
    if (!this.extraInfo && currencyData?.description) {
      this.extraInfo = [currencyData.description];
    }
    if (!this.extraInfo) {
      this.extraInfo = this.itemDataService.getJewellery(optionText)?.implicits;
    }
    if (!this.extraInfo) {
      this.extraInfo = this.itemDataService.getFlask(optionText)?.effects;
    }

    this.value = this.economyService.getCurrencyValue(optionText);
  }

  unhovered() {
    this.extraInfo = undefined;
    this.value = undefined;
  }

  toggleDropdown() {
    if (this.disabled()) return;
    this.open.update(open => !open);
    if (this.open()) {
      this.searchText.set('');
      // Set highlighted index to current selection or first option
      const currentIndex = this.filteredOptions().findIndex(opt => opt.value() === this.selectedValue());
      this.highlightedIndex.set(currentIndex >= 0 ? currentIndex : 0);
      if (this.searchable()) {
        setTimeout(() => {
          if (this.searchInput) {
            this.searchInput.nativeElement.focus();
          }
        }, 1);
      }
    } else {
      this.highlightedIndex.set(-1);
    }
  }

  selectOption(option: SelectOptionComponent, byKeyboard = false) {
    this.selectedValue.set(option.value());
    this.onChange(option.value());
    this.onTouched();

    if (!byKeyboard) {
      this.open.set(false);
      this.highlightedIndex.set(-1);
    }

    queueMicrotask(() => {
      this.selectedValueChange.emit(option.value());
    });
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    this.selectedValue.set(value);
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Input signals are read-only, so we can't set them directly
    // The disabled state is handled by the parent component
  }
}
